import React, { useState, useContext, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import uaParserJs from 'ua-parser-js';
import ReactJson from 'react-json-view';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import DeleteIcon from '@material-ui/icons/Delete';
import IconButton from '@material-ui/core/IconButton';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import Collapse from '@material-ui/core/Collapse';
import TableRow from '@material-ui/core/TableRow';
import Modal from '../../components/Modal/Modal';
import { Typography, TextField, Checkbox, FormControlLabel, Divider } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import ClearIcon from '@material-ui/icons/Clear';
import Link from '@material-ui/core/Link';
import DoneIcon from '@material-ui/icons/Done';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import SettingsIcon from '@material-ui/icons/Settings';
import LockIcon from '@material-ui/icons/Lock';
import Popover from '@material-ui/core/Popover';
import moment from 'moment-timezone';
import { getCookie } from '../../utils/helpers';
import { Context as UserContext } from '../../context/UserContext';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Required columns that cannot be disabled
const REQUIRED_COLUMNS = ['createdAt', 'offerName', 'email', 'status', 'delete'];

// Default visible columns (includes required + some optional)
const DEFAULT_COLUMNS = [
  'createdAt',
  'offerName',
  'processedResponseData.order_id',
  'email',
  'affId',
  'last4',
  'status',
  'delete',
];

// All available columns with their configuration
const ALL_COLUMN_DEFINITIONS = {
  // Required columns
  createdAt: { id: 'createdAt', label: 'Time', required: true },
  offerName: { id: 'offerName', label: 'Offer', required: true },
  email: { id: 'email', label: 'Email', required: true },
  status: { id: 'status', label: 'Status', required: true },
  delete: { id: 'delete', label: 'Delete', align: 'right', required: true },

  // Optional columns from Order model
  'processedResponseData.order_id': { id: 'processedResponseData.order_id', label: 'Sticky Order Id' },
  affId: { id: 'affId', label: 'Aff ID' },
  last4: { id: 'last4', label: 'Last 4 Digits' },
  firstName: { id: 'firstName', label: 'First Name' },
  lastName: { id: 'lastName', label: 'Last Name' },
  phone: { id: 'phone', label: 'Phone' },
  productName: { id: 'productName', label: 'Product Name' },
  productPrice: { id: 'productPrice', label: 'Product Price' },
  orderTotal: { id: 'orderTotal', label: 'Order Total' },
  shippingCity: { id: 'shippingCity', label: 'Shipping City' },
  shippingState: { id: 'shippingState', label: 'Shipping State' },
  shippingCountry: { id: 'shippingCountry', label: 'Shipping Country' },
  shippingZip: { id: 'shippingZip', label: 'Shipping Zip' },
  billingCity: { id: 'billingCity', label: 'Billing City' },
  billingState: { id: 'billingState', label: 'Billing State' },
  billingCountry: { id: 'billingCountry', label: 'Billing Country' },
  ipAddress: { id: 'ipAddress', label: 'IP Address' },
  creditCardType: { id: 'creditCardType', label: 'Payment Type' },
  campaignId: { id: 'campaignId', label: 'Campaign ID' },
  prospectId: { id: 'prospectId', label: 'Prospect ID' },
  everflowTransactionId: { id: 'everflowTransactionId', label: 'Everflow TX ID' },
  utm_source: { id: 'utm_source', label: 'UTM Source' },
  utm_medium: { id: 'utm_medium', label: 'UTM Medium' },
  promoCode: { id: 'promoCode', label: 'Promo Code' },
  gclid: { id: 'gclid', label: 'GCLID' },
  fbclid: { id: 'fbclid', label: 'FBCLID' },
  c1: { id: 'c1', label: 'C1' },
  c2: { id: 'c2', label: 'C2' },
  c3: { id: 'c3', label: 'C3' },
  sid: { id: 'sid', label: 'SID' },
  tid: { id: 'tid', label: 'TID' },
  cid: { id: 'cid', label: 'CID' },
  click_id: { id: 'click_id', label: 'Click ID' },
  fbc: { id: 'fbc', label: 'FBC' },
  fbp: { id: 'fbp', label: 'FBP' },
  flowIsComplete: { id: 'flowIsComplete', label: 'Flow Complete' },
  processed: { id: 'processed', label: 'Processed' },
  declinedByRA: { id: 'declinedByRA', label: 'Declined by RA' },
  declineReason: { id: 'declineReason', label: 'Decline Reason' },
  hasWebTv: { id: 'hasWebTv', label: 'Has WebTV' },
  upsellCount: { id: 'upsellCount', label: 'Upsell Count' },
};

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort, visibleColumns } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = visibleColumns
    .filter((colId) => colId !== 'status' && colId !== 'delete')
    .map((colId) => ALL_COLUMN_DEFINITIONS[colId])
    .filter(Boolean);

  // Add status and delete at the end
  if (visibleColumns.includes('status')) {
    headCells.push(ALL_COLUMN_DEFINITIONS.status);
  }
  if (visibleColumns.includes('delete')) {
    headCells.push(ALL_COLUMN_DEFINITIONS.delete);
  }

  return (
    <TableHead>
      <TableRow>
        <TableCell></TableCell>
        {headCells.map((headCell, index) => (
          <TableCell
            key={index}
            align={headCell.align || 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  visibleColumns: PropTypes.array.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  modalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  tableToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  columnSelector: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 280,
    maxHeight: 450,
  },
  columnSelectorHeader: {
    padding: theme.spacing(2, 2, 1, 2),
  },
  columnSelectorContent: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(0, 2),
  },
  columnSelectorFooter: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  searchField: {
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  columnItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
  },
  activeColumnItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  reorderButtons: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(1),
  },
  lockedIcon: {
    marginLeft: 'auto',
    fontSize: 16,
    color: theme.palette.text.disabled,
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
}));

export default function OrdersTable({
  orders,
  onDeleteOrder,
  ordersTotal,
  pagination,
  onPaginationChange,
  timezone = 'America/Los_Angeles',
}) {
  const parser = new uaParserJs();
  const classes = useStyles();
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [columnSearch, setColumnSearch] = useState('');

  const {
    state: { user: userState },
    updatePreferences,
  } = useContext(UserContext);

  // Extract actual user from the login response (which contains { user, token })
  const user = userState?.user;

  // Local state for visible columns - updates immediately on toggle
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);

  // Sync with user preferences when they change (e.g., on login or page load)
  useEffect(() => {
    const savedColumns = user?.preferences?.ordersTableColumns;
    if (savedColumns && savedColumns.length > 0) {
      // Ensure all required columns are present
      const missingRequired = REQUIRED_COLUMNS.filter((c) => !savedColumns.includes(c));
      if (missingRequired.length > 0) {
        // Add missing required columns at appropriate positions
        setVisibleColumns([...savedColumns, ...missingRequired]);
      } else {
        setVisibleColumns(savedColumns);
      }
    }
  }, [user?.preferences?.ordersTableColumns]);

  // Toggle column visibility (local state only, user must click Save)
  const handleColumnToggle = (columnId) => {
    if (REQUIRED_COLUMNS.includes(columnId)) return; // Can't toggle required columns

    // Compute new columns
    const newColumns = visibleColumns.includes(columnId)
      ? visibleColumns.filter((id) => id !== columnId)
      : [...visibleColumns, columnId];

    // Update local state immediately
    setVisibleColumns(newColumns);
  };

  // Move column left or right (local state only)
  const handleMoveColumn = (columnId, direction) => {
    const index = visibleColumns.indexOf(columnId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    // Don't move beyond bounds
    if (newIndex < 0 || newIndex >= visibleColumns.length) return;

    const newColumns = [...visibleColumns];
    [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];

    // Update local state
    setVisibleColumns(newColumns);
  };

  // Save all column changes to backend
  const handleSaveColumns = () => {
    if (user?._id) {
      updatePreferences(user._id, {
        ...user.preferences,
        ordersTableColumns: visibleColumns,
      });
    }
    handleSettingsClose();
  };

  // Get all available optional columns (filtering out required)
  const optionalColumns = useMemo(() => {
    return Object.entries(ALL_COLUMN_DEFINITIONS)
      .filter(([id]) => !REQUIRED_COLUMNS.includes(id))
      .map(([id, def]) => def);
  }, []);

  // Filter columns by search
  const filteredColumns = useMemo(() => {
    if (!columnSearch.trim()) return optionalColumns;
    const search = columnSearch.toLowerCase();
    return optionalColumns.filter(
      (col) => col.label.toLowerCase().includes(search) || col.id.toLowerCase().includes(search),
    );
  }, [columnSearch, optionalColumns]);

  function handleRequestSort(event, property) {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }

  function handleChangePage(event, newPage) {
    onPaginationChange({
      ...pagination,
      page: newPage,
    });
  }

  function handleChangeRowsPerPage(event) {
    onPaginationChange({
      limit: event.target.value,
      page: 0,
    });
  }

  function handleDeleteOrder(id) {
    setDeleteModalOpen(true);
    setOrderToDelete(id);
  }

  function handleSettingsClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleSettingsClose() {
    setAnchorEl(null);
    setColumnSearch('');
  }

  function renderOrderStatus(row) {
    // Transaction is approved (highest priority status)
    if (
      row.processed &&
      (row.processedResponseData?.response_code === '100' ||
        row.processedResponseData?.responsecode === '100')
    ) {
      return (
        <Tooltip title='Transaction approved'>
          <DoneIcon style={{ color: 'green' }} />
        </Tooltip>
      );
    }

    // Transaction is declined
    if (row.processed && row.processedResponseData) {
      const declinedMessage = row.processedResponseData?.error_message || 'Transaction declined';
      return (
        <Tooltip title={declinedMessage}>
          <ClearIcon color='error' />
        </Tooltip>
      );
    }

    // Order was declined by RA
    if (row.declinedByRA) {
      return (
        <Tooltip title={`RA Decline: ${row.declineReason}.`}>
          <ClearIcon color='error' />
        </Tooltip>
      );
    }

    // Pre-auth declined
    if (row.preAuthResponse && row.preAuthResponse?.response_code !== '100') {
      return (
        <Tooltip title={`Pre Auth Decline: ${row.preAuthResponse?.decline_reason}`}>
          <ClearIcon color='error' />
        </Tooltip>
      );
    }

    // Order was skipped by cron
    if (row.skipped) {
      return (
        <Tooltip title='Order was skipped by cron'>
          <NotInterestedIcon color='disabled' />
        </Tooltip>
      );
    }

    // Order processed but status unknown
    if (row.processed) {
      return (
        <Tooltip title='Order processed'>
          <DoneIcon style={{ color: 'orange' }} />
        </Tooltip>
      );
    }

    // Flow completed but not processed yet
    if (row.flowIsComplete) {
      return (
        <Tooltip title='Flow completed, waiting for processing'>
          <DoneIcon style={{ color: 'blue' }} />
        </Tooltip>
      );
    }

    // Flow not completed (earliest state)
    return (
      <Tooltip title='Flow not completed'>
        <ClearIcon style={{ color: 'gray' }} />
      </Tooltip>
    );
  }

  function renderCookie(str) {
    if (!str) {
      return null;
    }
    const cookieObject = str
      .split(';')
      .map((s) => s.trim())
      .sort()
      .reduce((acc, cookie) => {
        const [key, ...valueParts] = cookie.split('=');
        if (key) {
          acc[key] = valueParts.join('=');
        }
        return acc;
      }, {});

    return (
      <ReactJson
        src={cookieObject}
        collapsed={true}
        displayDataTypes={false}
        name={false}
        style={{ fontSize: '12px' }}
      />
    );
  }

  function renderDeviceInfo(str) {
    parser.setUA(str);
    const result = parser.getResult();

    return (
      <Box>
        <Typography variant='caption' display='block' gutterBottom>
          {result.browser.name} {result.browser.version} | {result.os.name} {result.os.version} |{' '}
          {result.device.vendor} {result.device.model} {result.device.type}
        </Typography>
      </Box>
    );
  }

  function getOrderId(order) {
    if (!order?.processedResponseData) return null;

    return order.processedResponseData.order_id || order.processedResponseData.orderid;
  }

  // Get nested value from object using dot notation
  function getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  // Render cell value for a given column
  function renderCellValue(row, columnId) {
    switch (columnId) {
      case 'createdAt':
        return (
          <Typography variant='caption' gutterBottom style={{ whiteSpace: 'nowrap' }}>
            <Tooltip title='Created'>
              <span>{moment.tz(row.createdAt, timezone).format('MMM DD HH:mm:ss')}</span>
            </Tooltip>
            {' -> '}
            <Tooltip title='Last updated'>
              <span>{moment.tz(row.updatedAt, timezone).format('MMM DD HH:mm:ss')}</span>
            </Tooltip>
          </Typography>
        );
      case 'offerName':
        return row.offerName;
      case 'processedResponseData.order_id':
        const orderId = getOrderId(row);
        return orderId ? (
          <Link
            target='_blank'
            href={`https://launch.sticky.io/admin/orders.php?show_details=show_details&show_folder=view_all&fromPost=1&act=&sequence=1&show_by_id=${orderId}`}
          >
            {orderId}
          </Link>
        ) : null;
      case 'email':
        return row.email;
      case 'last4':
        return row.creditCardType === 'paypal'
          ? 'PayPal'
          : row.creditCardType === 'googlepay'
            ? 'Google Pay'
            : row.creditCardType === 'applepay'
              ? 'Apple Pay'
              : row?.last4 || getCookie(row?.cookie, 'last4digits');
      case 'affId':
        return row?.affId?.split('&')[0];
      case 'orderTotal':
        return row.orderTotal ? `$${row.orderTotal.toFixed(2)}` : '';
      case 'flowIsComplete':
      case 'processed':
      case 'declinedByRA':
      case 'hasWebTv':
        return row[columnId] ? 'Yes' : 'No';
      default:
        // Handle nested values
        if (columnId.includes('.')) {
          return getNestedValue(row, columnId);
        }
        return row[columnId] ?? '';
    }
  }

  function Row(props) {
    const { row } = props;
    const [open, setOpen] = useState(false);

    // Build visible columns excluding status and delete (handled separately at end)
    const dataCols = visibleColumns.filter((id) => id !== 'status' && id !== 'delete');

    return (
      <>
        <TableRow key={row._id} size='small'>
          <TableCell>
            <IconButton aria-label='expand row' size='small' onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          {dataCols.map((colId) => (
            <TableCell
              key={colId}
              component={colId === 'createdAt' ? 'th' : undefined}
              align={ALL_COLUMN_DEFINITIONS[colId]?.align || 'left'}
            >
              {renderCellValue(row, colId)}
            </TableCell>
          ))}
          {visibleColumns.includes('status') && <TableCell>{renderOrderStatus(row)}</TableCell>}
          {visibleColumns.includes('delete') && (
            <TableCell align='right'>
              <IconButton
                color='secondary'
                aria-label='Delete order'
                component='span'
                className={classes.deleteIcon}
                onClick={() => handleDeleteOrder(row._id)}
              >
                <DeleteIcon />
              </IconButton>
            </TableCell>
          )}
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={visibleColumns.length + 1}>
            <Collapse in={open} timeout='auto' unmountOnExit>
              <Box margin={1}>
                <Typography variant='h6' gutterBottom component='div'>
                  Order details
                </Typography>
                <Table size='small' aria-label='purchases'>
                  <TableBody>
                    <TableRow>
                      <TableCell>Everflow transaction id:</TableCell>
                      <TableCell>{row.everflowTransactionId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Order source URL:</TableCell>
                      <TableCell
                        style={{
                          wordBreak: 'break-all',
                        }}
                      >
                        {row.orderSourceUrl}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>User cookies:</TableCell>
                      <TableCell
                        style={{
                          wordBreak: 'break-all',
                        }}
                      >
                        {renderCookie(row.cookie)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Device info:</TableCell>
                      <TableCell
                        style={{
                          wordBreak: 'break-all',
                        }}
                      >
                        {renderDeviceInfo(row.userAgent)}
                      </TableCell>
                    </TableRow>
                    {row?.processedResponseData?.orderTotal && (
                      <TableRow>
                        <TableCell>Order total:</TableCell>
                        <TableCell>{`$${row.processedResponseData.orderTotal}`}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  const settingsOpen = Boolean(anchorEl);

  return (
    <div className={classes.root}>
      <Modal open={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Box>
          <Typography variant='h6' gutterBottom>
            Are you sure you want to delete this order?
          </Typography>
        </Box>
        <Box className={classes.modalButtons}>
          <Button
            variant='contained'
            color='primary'
            style={{ marginRight: '10px' }}
            onClick={async () => {
              setDeleteModalOpen(false);
            }}
          >
            No
          </Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={async () => {
              await onDeleteOrder(orderToDelete);
              setDeleteModalOpen(false);
            }}
          >
            Yes
          </Button>
        </Box>
      </Modal>
      <Paper className={classes.paper}>
        <Box className={classes.tableToolbar}>
          <Tooltip title='Configure columns'>
            <IconButton onClick={handleSettingsClick} size='small'>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Popover
          open={settingsOpen}
          anchorEl={anchorEl}
          onClose={handleSettingsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box className={classes.columnSelector}>
            {/* Fixed Header */}
            <Box className={classes.columnSelectorHeader}>
              <Typography variant='subtitle1' gutterBottom style={{ fontWeight: 600 }}>
                Configure Columns
              </Typography>
              <TextField
                className={classes.searchField}
                placeholder='Search columns...'
                variant='outlined'
                size='small'
                value={columnSearch}
                onChange={(e) => setColumnSearch(e.target.value)}
                autoFocus
              />
            </Box>

            {/* Scrollable Content */}
            <Box className={classes.columnSelectorContent}>
              <Typography variant='caption' className={classes.sectionTitle}>
                Active Columns
              </Typography>
              {visibleColumns.map((colId, index) => {
                const col = ALL_COLUMN_DEFINITIONS[colId];
                if (!col) return null;
                const isRequired = REQUIRED_COLUMNS.includes(colId);
                const isFirst = index === 0;
                const isLast = index === visibleColumns.length - 1;

                return (
                  <Box key={colId} className={classes.activeColumnItem}>
                    <Box className={classes.reorderButtons}>
                      <IconButton
                        size='small'
                        disabled={isFirst}
                        onClick={() => handleMoveColumn(colId, 'up')}
                        style={{ padding: 2 }}
                      >
                        <ArrowUpwardIcon fontSize='small' />
                      </IconButton>
                      <IconButton
                        size='small'
                        disabled={isLast}
                        onClick={() => handleMoveColumn(colId, 'down')}
                        style={{ padding: 2 }}
                      >
                        <ArrowDownwardIcon fontSize='small' />
                      </IconButton>
                    </Box>
                    <Typography variant='body2' style={{ flex: 1 }}>
                      {col.label}
                    </Typography>
                    {isRequired ? (
                      <LockIcon className={classes.lockedIcon} />
                    ) : (
                      <IconButton
                        size='small'
                        onClick={() => handleColumnToggle(colId)}
                        style={{ padding: 2 }}
                      >
                        <ClearIcon fontSize='small' />
                      </IconButton>
                    )}
                  </Box>
                );
              })}

              <Divider style={{ margin: '12px 0 8px 0' }} />

              <Typography variant='caption' className={classes.sectionTitle}>
                Available Columns ({filteredColumns.filter((c) => !visibleColumns.includes(c.id)).length})
              </Typography>
              {filteredColumns
                .filter((col) => !visibleColumns.includes(col.id))
                .map((col) => (
                  <Box key={col.id} className={classes.columnItem}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={false} onChange={() => handleColumnToggle(col.id)} size='small' />
                      }
                      label={col.label}
                    />
                  </Box>
                ))}
            </Box>

            {/* Fixed Footer */}
            <Box className={classes.columnSelectorFooter}>
              <Button variant='contained' color='primary' size='small' onClick={handleSaveColumns}>
                Save
              </Button>
            </Box>
          </Box>
        </Popover>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby='tableTitle'
            aria-label='enhanced table'
            size='small'
            padding='checkbox'
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={ordersTotal}
              visibleColumns={visibleColumns}
            />
            <TableBody>
              {stableSort(orders, getComparator(order, orderBy)).map((row, index) => (
                <Row key={index} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={ordersTotal}
          rowsPerPageOptions={[25, 50, 100, 500, 1000]}
          rowsPerPage={pagination.limit}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
